import { Option, Schema } from "effect";
import { SupportTicketId } from "../../ids/definitions";
import { MongoUserId } from "../../ids/mongo-ids";

/**
 * Sentinel value the chat-widget side emits when a *customer* (rather
 * than a merchant user) is typing/viewing inside a widget conversation.
 * Customers don't have Mongo user ids, so subscribers switch on this
 * literal instead of treating it as a missing user.
 */
export const WIDGET_CUSTOMER_PRESENCE_USER_ID = "Customer";
export const WidgetCustomerPresenceUserId = Schema.Literal(
  WIDGET_CUSTOMER_PRESENCE_USER_ID,
);
export type WidgetCustomerPresenceUserId =
  typeof WidgetCustomerPresenceUserId.Type;

/**
 * Userid carried on a presence event: either a branded merchant user id
 * or the widget-customer sentinel. One union keeps the merchant-side
 * typing strict (`MongoUserId` everywhere) while still allowing the
 * widget side to publish without a real user id.
 */
export const PresenceUserId = Schema.Union(
  MongoUserId,
  WidgetCustomerPresenceUserId,
);
export type PresenceUserId = typeof PresenceUserId.Type;

const decodePresenceUserIdOption = Schema.decodeUnknownOption(PresenceUserId);

/**
 * Convert a raw wire string (from an SSE event or REST snapshot) into a
 * branded {@link PresenceUserId}. Returns `undefined` when the value isn't
 * a recognized presence id — callers should drop those entries rather than
 * surface a raw, unparsable id in the UI.
 */
export const parsePresenceUserId = (raw: string): PresenceUserId | undefined =>
  Option.getOrUndefined(decodePresenceUserIdOption(raw));

export const TypingNotification = Schema.Struct({
  _tag: Schema.Literal("typing"),
  supportTicketId: SupportTicketId,
  userId: PresenceUserId,
});
export type TypingNotification = typeof TypingNotification.Type;

export const PresenceViewingNotification = Schema.Struct({
  _tag: Schema.Literal("presenceViewing"),
  supportTicketId: SupportTicketId,
  userId: PresenceUserId,
});
export type PresenceViewingNotification =
  typeof PresenceViewingNotification.Type;

export const PresenceClearedActivity = Schema.Literal(
  "viewing",
  "typing",
  "all",
);
export type PresenceClearedActivity = typeof PresenceClearedActivity.Type;

export const PresenceClearedNotification = Schema.Struct({
  _tag: Schema.Literal("presenceCleared"),
  supportTicketId: SupportTicketId,
  userId: PresenceUserId,
  activity: PresenceClearedActivity,
});
export type PresenceClearedNotification =
  typeof PresenceClearedNotification.Type;

export const PresenceNotification = Schema.Union(
  TypingNotification,
  PresenceViewingNotification,
  PresenceClearedNotification,
);
export type PresenceNotification = typeof PresenceNotification.Type;

export const PresenceNotificationJson = Schema.parseJson(PresenceNotification);

export type PresenceNotificationTag = PresenceNotification["_tag"];

/**
 * Merchant-only presence input (no widget-customer variant): the
 * merchant control endpoint only ever publishes events for the
 * authenticated merchant user.
 */
export const NotifyTypingInput = Schema.Struct({
  supportTicketId: SupportTicketId,
  userId: MongoUserId,
  name: Schema.String,
});
export type NotifyTypingInput = typeof NotifyTypingInput.Type;

export const NotifyPresenceInput = Schema.Union(
  Schema.Struct({
    event: Schema.Literal("presence.viewing"),
    supportTicketId: SupportTicketId,
    userId: MongoUserId,
  }),
  Schema.Struct({
    event: Schema.Literal("presence.typing"),
    supportTicketId: SupportTicketId,
    userId: MongoUserId,
    /** Writing a reply to the customer, not an internal note; Apple Messages
     * shows them the typing. */
    customerSees: Schema.optional(Schema.Boolean),
  }),
  Schema.Struct({
    event: Schema.Literal("presence.cleared"),
    userId: MongoUserId,
    supportTicketId: Schema.optional(SupportTicketId),
    activity: Schema.optional(Schema.Literal("typing", "viewing", "all")),
  }),
);
export type NotifyPresenceInput = typeof NotifyPresenceInput.Type;

/**
 * Service-level input accepted by `PresenceService.notifyPresence`. Distinct
 * from `NotifyPresenceInput` (the merchant RPC payload) because the widget
 * side publishes events for the customer sentinel, which is not a valid
 * `MongoUserId`. Both merchant and widget callers funnel through the same
 * service, so it accepts the union.
 */
export const PresenceServiceNotifyInput = Schema.Union(
  Schema.Struct({
    event: Schema.Literal("presence.viewing"),
    supportTicketId: SupportTicketId,
    userId: PresenceUserId,
  }),
  Schema.Struct({
    event: Schema.Literal("presence.typing"),
    supportTicketId: SupportTicketId,
    userId: PresenceUserId,
  }),
  Schema.Struct({
    event: Schema.Literal("presence.cleared"),
    userId: PresenceUserId,
    supportTicketId: Schema.optional(SupportTicketId),
    activity: Schema.optional(Schema.Literal("typing", "viewing", "all")),
  }),
);
export type PresenceServiceNotifyInput = typeof PresenceServiceNotifyInput.Type;
